import React, { useState, useCallback, useEffect } from 'react';
import { Input, Box, List, ListItem } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import './SearchBarAutocomplete.scss';

interface SearchSuggestion {
  id: string;
  title: {
    en: string;
    uk: string;
  };
  [key: string]: any;
}

interface SearchBarAutocompleteProps {
  placeholder?: string;
  width?: number;
  height?: number;
  isClearable?: boolean;
  onSearch: (query: string) => void;
  onSuggestionsFetch?: (query: string) => Promise<SearchSuggestion[]>;
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void;
  minCharsForSuggestions?: number;
  maxSuggestions?: number;
  debounceDelay?: number;
}

export const SearchBarAutocomplete: React.FC<SearchBarAutocompleteProps> = ({
  placeholder = 'Search...',
  width = 250,
  height = 36,
  isClearable = true,
  onSearch,
  onSuggestionsFetch,
  onSuggestionSelect,
  minCharsForSuggestions = 2,
  maxSuggestions = 5,
  debounceDelay = 300,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Debounce timer
  useEffect(() => {
    if (query.length >= minCharsForSuggestions && onSuggestionsFetch) {
      const timer = setTimeout(async () => {
        setIsLoading(true);
        try {
          const results = await onSuggestionsFetch(query);
          setSuggestions(results.slice(0, maxSuggestions));
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceDelay);

      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [
    query,
    minCharsForSuggestions,
    maxSuggestions,
    debounceDelay,
    onSuggestionsFetch,
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSearch = useCallback(() => {
    if (query.trim()) {
      onSearch(query);
      setShowSuggestions(false);
    }
  }, [query, onSearch]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    }
    setShowSuggestions(false);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <Box
      className='search-bar-autocomplete'
      position='relative'
      width={`${width}px`}
    >
      <Input
        value={query}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        placeholder={t(placeholder)}
        height={`${height}px`}
        className='search-bar-autocomplete__input'
      />
      {isClearable && query && (
        <button
          className='search-bar-autocomplete__clear'
          onClick={handleClear}
          type='button'
        >
          ×
        </button>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <List className='search-bar-autocomplete__suggestions'>
          {suggestions.map(suggestion => (
            <ListItem
              key={suggestion.id}
              className='search-bar-autocomplete__suggestion-item'
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.title.en || suggestion.title.uk}
            </ListItem>
          ))}
        </List>
      )}
      {isLoading && (
        <Box className='search-bar-autocomplete__loading'>
          {t('common.loading')}...
        </Box>
      )}
    </Box>
  );
};
